import ky, {
	type AfterResponseHook,
	type BeforeErrorHook,
	type BeforeRequestHook,
	type BeforeRetryHook,
	type Input,
	type KyInstance,
	type Options,
} from 'ky';

// Live, mutable defaults that will be injected at **call time**. This can hold
// any Ky `Options` fields (timeout, headers, retry, …). We start with a
// sensible `prefixUrl` fallback so that code never generates an absolute URL
// of "undefined/…" if the consumer forgets to configure.
let config: {
	/**
	 * to make your service layer isomorphic we always add this option.
	 * it's recommended to always provide origin url on server side
	 * on client side it falls back window.location.origin
	 */
	prefixUrl: string;
	hooks?: {
		afterResponse?: AfterResponseHook;
		beforeRequest?: BeforeRequestHook;
		beforeRetry?: BeforeRetryHook;
		beforeError?: BeforeErrorHook;
	};
} = {
	prefixUrl: '/',
};

/**
 * rename ky instance to fetch instance
 */
interface FetchInstance extends KyInstance {}

// Consumers may call this at any time. All subsequent requests (even from
// previously created `extend()`ed instances) will see the updated defaults
// because we merge `config` at the moment the request is issued.
export function configure(options: {
	/**
	 * to make your service layer isomorphic we always add this option.
	 * it's recommended to always provide origin url on server side
	 * on client side it falls back window.location.origin
	 */
	prefixUrl: string;
	hooks?: {
		afterResponse?: AfterResponseHook;
		beforeRequest?: BeforeRequestHook;
		beforeRetry?: BeforeRetryHook;
		beforeError?: BeforeErrorHook;
	};
}): void {
	config = { ...config, ...options };
	config.prefixUrl = options.prefixUrl || '/';
}

type Mutable<T> = {
	-readonly [P in keyof T]: T[P];
};

function mergeOptions(parentOptions: Options, options: Options): Options {
	// merge hooks
	if (!options.prefixUrl) {
		options.prefixUrl = parentOptions.prefixUrl || config.prefixUrl;
	}
	return options;
}

function addWrapper(ky: FetchInstance, kyOptions?: Options) {
	const requestMethods = [
		'get',
		'post',
		'put',
		'patch',
		'head',
		'delete',
	] as const;

	// Core callable that injects the **current** global defaults on every call.
	const defaultInstance = ((input: Input, options?: Options) => {
		const mergedOptions: Options = mergeOptions(kyOptions || {}, options || {});
		if (
			typeof input === 'string' &&
			input.startsWith('/') &&
			config.prefixUrl
		) {
			// warn
			console.warn('Do not use leading slash in the input url');
			// remove the leading slash
			input = input.slice(1);
		}
		return ky(input, mergedOptions);
	}) as FetchInstance;

	const baseInstance: Partial<Mutable<FetchInstance>> = defaultInstance;

	for (const method of requestMethods) {
		baseInstance[method] = (input: Input, options?: Options) =>
			defaultInstance(input, { ...options, method });
	}
	// Preserve Ky's `create` / `extend`, but ensure the resulting instance keeps
	// the same dynamic-defaults behaviour by wrapping it again.
	baseInstance.create = (newDefaults) => ky.create(newDefaults);
	// `extend` is overloaded in Ky – we forward the call and then wrap.
	baseInstance.extend = (newDefaults) =>
		addWrapper(
			ky.extend(newDefaults),
			typeof newDefaults === 'function'
				? newDefaults(kyOptions || {})
				: kyOptions,
		);
	baseInstance.stop = ky.stop;
	return baseInstance as FetchInstance;
}

function createFetchInstance(): FetchInstance {
	return addWrapper(
		ky.create({
			prefixUrl: config.prefixUrl,
			retry: 0,
			timeout: false,
			hooks: {
				afterResponse: [
					(...args) => {
						if (config.hooks?.afterResponse) {
							return config.hooks.afterResponse(...args);
						}
					},
				],
				beforeRequest: [
					(...args) => {
						if (config.hooks?.beforeRequest) {
							return config.hooks.beforeRequest(...args);
						}
					},
				],
				beforeRetry: [
					(...args) => {
						if (config.hooks?.beforeRetry) {
							return config.hooks.beforeRetry(...args);
						}
					},
				],
				beforeError: [
					(e) => {
						if (config.hooks?.beforeError) {
							return config.hooks.beforeError(e);
						}
						return e;
					},
				],
			},
		}),
	);
}

/**
 * enhanced fetch instance with the given configuration
 * configured with prefixUrl & hooks with application wide common fetch options
 * do not use leading slash in the input url
 */
const _fetchInstance = createFetchInstance();

export default _fetchInstance;

/**
 * Direct access to the unconfigured fetch instance (rarely needed).
 */
export const baseFetch: FetchInstance = ky;

// Re-export Ky hook types for consumers
export type {
	AfterResponseHook,
	BeforeErrorHook,
	BeforeRequestHook,
	BeforeRetryHook,
	Hooks,
	Input,
	Options,
} from 'ky';
export { HTTPError, TimeoutError } from 'ky';
export type { FetchInstance };
